import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  HelpCircle,
  KeyRound,
  Loader2,
  Mail,
  ShieldCheck,
  ShoppingBag,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getAuthMode, sendOtp, verifyOtp, devLogin } from '../api';
import byteLockup from '../assets/byte-lockup.png';
import authGradient from '../assets/byte-auth-gradient.svg';

const PERSONAS: { yumId: string; label: string }[] = [
  { yumId: 'ADM0001', label: 'Admin (global)' },
  { yumId: 'LEG2000', label: 'Legal (Europe)' },
  { yumId: 'FBC3000', label: 'FBC (Europe, read-only)' },
  { yumId: 'FIN4000', label: 'Finance (global)' },
  { yumId: 'MYD7020', label: 'Franchisee — AmRest (Poland)' },
  { yumId: 'MYD9500', label: 'Franchisee — AmRest France' },
  // Taco Bell UK franchisee (Formsite import) — open /adopt/seed-tb-uk to see the imported suite.
  { yumId: 'MYD7700', label: 'Franchisee — Taco Bell UK' },
  // No grant, no synced match → hits the "Select your franchisee" step on any
  // in-scope link (e.g. /adopt/seed-kfc-pol). Use to test the self-select flow.
  { yumId: 'MYD0001', label: 'Franchisee — new (unmatched)' },
];

// Franchisee-lens value props — full rationale in
// docs/product/value-proposition.md (Franchisee gains).
const VALUE_PROPS: {
  icon: typeof ShoppingBag;
  title: string;
  body: string;
  accent: 'warm' | 'purple';
}[] = [
  {
    icon: ShoppingBag,
    title: 'Choose the right products',
    body: 'Browse solutions that drive the biggest impact for your business.',
    accent: 'warm',
  },
  {
    icon: UserRound,
    title: 'Your details, ready to go',
    body: 'We prefill what we already know so you can move forward faster.',
    accent: 'purple',
  },
  {
    icon: ShieldCheck,
    title: 'Clear terms, instant confirmation',
    body: 'Review with confidence and get started right away.',
    accent: 'purple',
  },
];

type Step = 'yumId' | 'code';

/** Header copy for the auth screen. Overridable so the adoption link can show its own context. */
export interface LoginIntro {
  eyebrow: string;
  heading: ReactNode;
  subtitle: string;
}
const DEFAULT_INTRO: LoginIntro = {
  eyebrow: 'Invite Only',
  heading: (
    <>
      Get started
      <br className="hidden sm:block" /> with <span className="bg-byte-gradient bg-clip-text text-transparent">Byte</span> today.
    </>
  ),
  subtitle: 'A simpler way to review products, confirm your details, and complete adoption in one guided flow.',
};

function redirectAfterLogin(override?: string): void {
  const returnTo = override || sessionStorage.getItem('returnTo');
  sessionStorage.removeItem('returnTo');
  window.location.href = returnTo || '/';
}

export function LoginPage({ intro = DEFAULT_INTRO, returnTo }: { intro?: LoginIntro; returnTo?: string } = {}) {
  const { setTokens } = useAuth();
  const { data: authMode } = useQuery({ queryKey: ['auth', 'mode'], queryFn: getAuthMode });

  const [step, setStep] = useState<Step>('yumId');
  const [yumId, setYumId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Test mode (local/E2E): a persona picker stands in for the OTP email round-trip.
  const [personaBusy, setPersonaBusy] = useState<string | null>(null);

  const errorMessage = (e: unknown): string =>
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    'Something went wrong. Please try again.';

  const onSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!yumId.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sendOtp(yumId.trim());
      setStep('code');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const tokens = await verifyOtp(yumId.trim(), code);
      setTokens(tokens.accessToken, tokens.refreshToken ?? '');
      redirectAfterLogin(returnTo);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const loginAs = async (persona: string) => {
    setPersonaBusy(persona);
    try {
      const idToken = await devLogin(persona);
      setTokens(idToken, '');
      redirectAfterLogin(returnTo);
    } catch {
      setPersonaBusy(null);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface-canvas font-sans text-byte-black">
      {/* Decorative brand layer — an organic corner accent anchored top-right (theme.md). */}
      <img
        src={authGradient}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 hidden h-full w-[55%] object-cover object-right-top lg:block xl:w-[50%]"
      />

      <div className="relative z-10 grid min-h-screen grid-cols-1 gap-y-9 px-6 pt-10 lg:grid-cols-[58%_42%] lg:grid-rows-[auto_auto_1fr_auto] lg:gap-y-0 lg:px-14 lg:pb-0 lg:pt-12">
        {/* ── Header block ───────────────────────────────────────────── */}
        <div className="lg:col-start-1 lg:row-start-1 lg:pr-12">
          <img src={byteLockup} alt="Byte by Yum!" className="h-9 w-auto" />

          <span className="mt-7 inline-flex items-center gap-2 rounded-full bg-status-info-soft px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-byte-purple">
            <ShieldCheck className="h-3.5 w-3.5" />
            {intro.eyebrow}
          </span>

          <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-byte-black sm:text-5xl xl:text-6xl">
            {intro.heading}
          </h1>

          <p className="mt-4 max-w-md text-base leading-relaxed text-graphite-600">
            {intro.subtitle}
          </p>
        </div>

        {/* ── Auth card ──────────────────────────────────────────────── */}
        <div className="lg:col-start-2 lg:row-span-4 lg:row-start-1 lg:flex lg:items-center lg:justify-start lg:pl-0">
          <div className="mx-auto w-full max-w-md rounded-3xl border border-border-subtle bg-white p-7 shadow-md sm:p-9 lg:mx-0 lg:-ml-8">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-byte-red/[0.07] to-byte-purple/[0.10]">
              <svg
                className="h-10 w-10"
                viewBox="0 0 48 48"
                fill="none"
                stroke="url(#shieldGrad)"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <defs>
                  <linearGradient id="shieldGrad" x1="8" y1="6" x2="40" y2="42" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#F5A31B" />
                    <stop offset="0.5" stopColor="#EF233C" />
                    <stop offset="1" stopColor="#662D91" />
                  </linearGradient>
                </defs>
                <path d="M24 6 L38 11 V21 C38 30 32 36.5 24 40 C16 36.5 10 30 10 21 V11 Z" />
                <rect x="18.5" y="20" width="11" height="8.5" rx="2" />
                <path d="M21 20 V17.5 a3 3 0 0 1 6 0 V20" />
              </svg>
            </span>

            {authMode === undefined ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-graphite-400" />
              </div>
            ) : authMode === 'test' ? (
              // ── Local dev — persona picker (no OTP email) ──
              <div className="mt-5">
                <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-graphite-400">
                  Local dev — sign in as a persona
                </p>
                <div className="flex flex-col gap-1.5">
                  {PERSONAS.map((p) => (
                    <button
                      key={p.yumId}
                      onClick={() => loginAs(p.yumId)}
                      disabled={personaBusy !== null}
                      className="flex items-center justify-between rounded-lg border border-border-subtle px-3 py-2 text-left text-sm text-graphite-700 transition hover:bg-surface-muted disabled:opacity-50"
                    >
                      <span>{p.label}</span>
                      <span className="font-mono text-xs text-graphite-400">
                        {personaBusy === p.yumId ? '…' : p.yumId}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : step === 'yumId' ? (
              // ── Step 1 — enter Yum ID ──
              <form onSubmit={onSendCode}>
                <h2 className="mt-5 text-center text-2xl font-bold text-byte-black">Sign in</h2>
                <p className="mx-auto mt-2 max-w-[18rem] text-center text-sm leading-relaxed text-graphite-500">
                  Enter your Yum ID and we&apos;ll email you a one-time sign-in code.
                </p>

                <label htmlFor="yumId" className="mt-6 block text-sm font-medium text-graphite-700">
                  Yum ID
                </label>
                <input
                  id="yumId"
                  type="text"
                  autoComplete="username"
                  autoFocus
                  value={yumId}
                  onChange={(e) => setYumId(e.target.value)}
                  placeholder="e.g. abc1234"
                  className="mt-1.5 w-full rounded-xl border border-border-subtle px-4 py-3 text-sm text-byte-black outline-none transition focus:border-byte-purple focus:ring-2 focus:ring-byte-purple/20"
                />

                {error && <p className="mt-3 text-sm text-status-danger">{error}</p>}

                <button
                  type="submit"
                  disabled={!yumId.trim() || busy}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-byte-gradient px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 active:scale-[0.99] disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  {busy ? 'Sending…' : 'Email me a code'}
                </button>
              </form>
            ) : (
              // ── Step 2 — enter code ──
              <form onSubmit={onVerify}>
                <h2 className="mt-5 text-center text-2xl font-bold text-byte-black">Enter your code</h2>
                <p className="mx-auto mt-2 max-w-[18rem] text-center text-sm leading-relaxed text-graphite-500">
                  We sent a 6-digit code to the email on file for <strong>{yumId.trim()}</strong>.
                </p>

                <label htmlFor="code" className="mt-6 block text-sm font-medium text-graphite-700">
                  Sign-in code
                </label>
                <input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="mt-1.5 w-full rounded-xl border border-border-subtle px-4 py-3 text-center font-mono text-lg tracking-[0.4em] text-byte-black outline-none transition focus:border-byte-purple focus:ring-2 focus:ring-byte-purple/20"
                />

                {error && <p className="mt-3 text-sm text-status-danger">{error}</p>}

                <button
                  type="submit"
                  disabled={code.length !== 6 || busy}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-byte-gradient px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 active:scale-[0.99] disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  {busy ? 'Verifying…' : 'Verify & sign in'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep('yumId');
                    setCode('');
                    setError(null);
                  }}
                  disabled={busy}
                  className="mt-4 flex items-center gap-1.5 text-xs text-graphite-400 transition hover:text-graphite-600 disabled:opacity-50"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Use a different Yum ID
                </button>
              </form>
            )}

            <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-graphite-400">
              <HelpCircle className="h-3.5 w-3.5" />
              Need help? Contact your Byte team.
            </p>
          </div>
        </div>

        {/* ── Value props ────────────────────────────────────────────── */}
        <ul className="flex flex-col gap-5 lg:col-start-1 lg:row-start-2 lg:mt-10 lg:max-w-md lg:gap-6 lg:pr-12">
          {VALUE_PROPS.map(({ icon: Icon, title, body, accent }) => (
            <li key={title} className="flex items-start gap-4">
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                  accent === 'warm'
                    ? 'bg-surface-warm text-byte-red ring-1 ring-byte-red/15'
                    : 'bg-status-info-soft text-byte-purple ring-1 ring-byte-purple/15'
                }`}
              >
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-byte-black">{title}</p>
                <p className="mt-0.5 text-sm leading-relaxed text-graphite-500">{body}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <footer className="relative -mx-6 mt-8 flex flex-col items-start gap-2 border-t border-border-subtle bg-surface-canvas px-6 py-6 text-xs text-graphite-400 sm:flex-row sm:items-center sm:justify-between lg:-mx-14 lg:col-span-2 lg:col-start-1 lg:row-start-4 lg:mt-0 lg:px-14">
          <span>© {year} Yum! Brands, Inc.</span>
          {/* Privacy/Terms links intentionally omitted until Legal supplies the canonical URLs —
              dead links are worse than none on a sign-in page. */}
          <span>For internal and invited franchise partner use.</span>
        </footer>
      </div>
    </div>
  );
}
